/**
 * MarkupMarginToggle (U5): the markup-vs-margin control. PRODUCT LAW: the two
 * must never be ambiguous — this control always names the active mode, shows
 * the exact formula it implies, and shows the equivalent figure in the other
 * mode. Margin is schema-bounded 0 ≤ g < 100 (%).
 *
 * The conversion shown here is display-only; authoritative pricing math is
 * the shared engine (U3).
 */
import { useId, type JSX, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';
import { NumericInput } from './NumericInput';
import { formatNumber } from './format';

/**
 * UI-level mode label. Structurally identical to the shared `mode` enum on
 * markup DTOs (`{ mode: 'markup' | 'margin', pct }` per ARCHITECTURE.md) —
 * feature code should pass values typed by @buildledger/shared (U2).
 */
export type PricingMode = 'markup' | 'margin';

export interface MarkupMarginToggleProps {
  mode: PricingMode;
  /** Percentage, e.g. 25 = 25%. */
  pct: number | null;
  onModeChange: (mode: PricingMode) => void;
  onPctChange: (pct: number | null) => void;
  label?: ReactNode;
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
}

const MODES: ReadonlyArray<{ id: PricingMode; label: string }> = [
  { id: 'markup', label: 'Markup' },
  { id: 'margin', label: 'Margin' },
];

export function MarkupMarginToggle({
  mode,
  pct,
  onModeChange,
  onPctChange,
  label = 'Pricing',
  size = 'md',
  disabled,
  className,
}: MarkupMarginToggleProps): JSX.Element {
  const groupId = useId();

  function handleSegKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown'
    ) {
      event.preventDefault();
      onModeChange(mode === 'markup' ? 'margin' : 'markup');
    }
  }

  const p = pct ?? 0;
  const factor = mode === 'markup' ? 1 + p / 100 : 1 - p / 100;
  // Equivalent figure in the other mode: markup m ↔ margin g, g = m/(1+m).
  const equivalent =
    mode === 'markup'
      ? p <= -100
        ? null
        : (p / (100 + p)) * 100
      : p >= 100
        ? null
        : (p / (100 - p)) * 100;

  return (
    <div className={cx('bl-mmt', className)}>
      <span className="bl-field__label" id={`${groupId}-label`}>
        {label}
      </span>
      <div className="bl-mmt__row">
        <div
          className="bl-mmt__seg"
          role="radiogroup"
          aria-labelledby={`${groupId}-label`}
          onKeyDown={handleSegKeyDown}
        >
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={mode === m.id}
              tabIndex={mode === m.id ? 0 : -1}
              className="bl-mmt__mode"
              disabled={disabled}
              onClick={() => onModeChange(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <span className="bl-mmt__pct">
          <NumericInput
            value={pct}
            onValueChange={onPctChange}
            min={0}
            max={mode === 'margin' ? 99.99 : undefined}
            decimals={2}
            trim
            step={1}
            size={size === 'sm' ? 'sm' : 'md'}
            suffix="%"
            disabled={disabled}
            aria-label={`${mode} percentage`}
            full
          />
        </span>
      </div>
      <div className="bl-mmt__formula">
        {mode === 'markup' ? (
          <>
            <span>
              <strong>Markup</strong> on cost:
            </span>
            <code className="bl-num">price = cost × {formatNumber(factor, { decimals: 4, trim: true })}</code>
            {equivalent !== null && (
              <span className="bl-mmt__equiv">
                = {formatNumber(equivalent, { decimals: 1 })}% margin
              </span>
            )}
          </>
        ) : (
          <>
            <span>
              <strong>Margin</strong> of price:
            </span>
            <code className="bl-num">price = cost ÷ {formatNumber(factor, { decimals: 4, trim: true })}</code>
            {equivalent !== null && (
              <span className="bl-mmt__equiv">
                = {formatNumber(equivalent, { decimals: 1 })}% markup
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
